import React, { useCallback, useEffect, useState } from 'react';
import { Layout } from '../components/Layout';
import { apiService, JobStatus } from '../services/api';

const JOB_LABELS: Record<string, string> = {
  'employee-sync': 'Employee sync',
  'supplier-sync': 'Supplier sync',
  'supplier-invoice-sync': 'Supplier invoice sync',
  'fortnox-pending-sync': 'Fortnox pending sync',
  'being-paid': 'Being-paid processor',
  'poll-bank': 'Bank poll (Citi SFTP)',
  'third-party-status-sync': 'Third-party status sync',
};

// Jobs wrapped in backend/src/services/fortnoxSyncLock.ts's runExclusive —
// they share one mutex so only one of them ever runs against Fortnox at a time.
const FORTNOX_JOB_NAMES = new Set([
  'employee-sync',
  'supplier-sync',
  'supplier-invoice-sync',
  'fortnox-pending-sync',
  'being-paid',
  'third-party-status-sync',
]);

function formatTime(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString();
}

function formatInterval(ms: number | null): string {
  if (!ms) return '—';
  const minutes = ms / 60000;
  if (minutes < 1) return `${Math.round(ms / 1000)}s`;
  if (minutes < 60) return `${minutes % 1 === 0 ? minutes : minutes.toFixed(1)}min`;
  const hours = minutes / 60;
  return `${hours % 1 === 0 ? hours : hours.toFixed(1)}h`;
}

interface JobTableProps {
  jobs: JobStatus[];
  runningName: string | null;
  blockedReason?: (job: JobStatus) => string | undefined;
  onRun: (name: string) => void;
}

function JobTable({ jobs, runningName, blockedReason, onRun }: JobTableProps) {
  return (
    <div className="bg-white border border-border/60 rounded-xl overflow-hidden">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border/60 text-left text-xs text-muted-foreground">
            <th className="px-4 py-3 font-medium">Job</th>
            <th className="px-4 py-3 font-medium">Interval</th>
            <th className="px-4 py-3 font-medium">Status</th>
            <th className="px-4 py-3 font-medium">Last run</th>
            <th className="px-4 py-3 font-medium">Last result</th>
            <th className="px-4 py-3 font-medium"></th>
          </tr>
        </thead>
        <tbody>
          {jobs.map((job) => {
            const blocked = blockedReason?.(job);
            const isTriggering = runningName === job.name;
            const disabled = job.isRunning || isTriggering || !!blocked;
            return (
              <tr key={job.name} className="border-b border-border/60 last:border-0">
                <td className="px-4 py-3 font-medium text-foreground">{JOB_LABELS[job.name] || job.name}</td>
                <td className="px-4 py-3 text-muted-foreground">{formatInterval(job.intervalMs)}</td>
                <td className="px-4 py-3">
                  {job.isExecuting ? (
                    <span className="inline-flex items-center gap-1.5 text-amber-600">
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
                      Running
                    </span>
                  ) : job.isRunning ? (
                    <span className="inline-flex items-center gap-1.5 text-blue-600">
                      <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse" />
                      Queued
                    </span>
                  ) : (
                    <span className="text-muted-foreground">Not running</span>
                  )}
                </td>
                <td className="px-4 py-3 text-muted-foreground">{formatTime(job.lastStartedAt)}</td>
                <td className="px-4 py-3">
                  {job.lastSuccess === null ? (
                    <span className="text-muted-foreground">—</span>
                  ) : job.lastSuccess ? (
                    <span className="text-green-600">Success</span>
                  ) : (
                    <span className="text-destructive" title={job.lastError || undefined}>
                      Failed{job.lastError ? `: ${job.lastError}` : ''}
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  <button
                    onClick={() => onRun(job.name)}
                    disabled={disabled}
                    title={!job.isRunning && !isTriggering ? blocked : undefined}
                    className="px-3 py-1.5 text-xs bg-foreground text-background rounded-md hover:opacity-90 disabled:opacity-50 transition-opacity"
                  >
                    {job.isRunning || isTriggering ? 'Running…' : 'Run now'}
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function Jobs() {
  const [jobs, setJobs] = useState<JobStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [runningName, setRunningName] = useState<string | null>(null);
  const [error, setError] = useState<string | undefined>();

  const loadJobs = useCallback(async () => {
    try {
      const { jobs: rows } = await apiService.getJobs();
      setJobs(rows);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load jobs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadJobs();
    const interval = setInterval(loadJobs, 5000);
    return () => clearInterval(interval);
  }, [loadJobs]);

  async function handleRun(name: string) {
    setRunningName(name);
    setError(undefined);
    try {
      await apiService.runJob(name);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to run job');
    } finally {
      setRunningName(null);
      loadJobs();
    }
  }

  const fortnoxJobs = jobs.filter((j) => FORTNOX_JOB_NAMES.has(j.name));
  const otherJobs = jobs.filter((j) => !FORTNOX_JOB_NAMES.has(j.name));
  // Several Fortnox jobs can show isRunning at once (queued behind the
  // shared lock in fortnoxSyncLock.ts) — isExecuting is the one actually
  // holding the lock and calling Fortnox right now.
  const runningFortnoxJob = fortnoxJobs.find((j) => j.isExecuting);

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Jobs</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Background sync jobs — status and manual triggers.
          </p>
        </div>

        {error && (
          <div className="bg-destructive/10 border border-destructive/30 text-destructive text-sm rounded-md px-4 py-2">
            {error}
          </div>
        )}

        {loading ? (
          <div className="bg-white border border-border/60 rounded-xl p-8">
            <p className="text-sm text-muted-foreground">Loading...</p>
          </div>
        ) : (
          <div className="space-y-8">
            {fortnoxJobs.length > 0 && (
              <div className="space-y-2">
                <div>
                  <h2 className="text-sm font-semibold text-foreground">Fortnox jobs</h2>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    These share a single Fortnox API queue and never run at the same time — starting one while
                    another is running is blocked{runningFortnoxJob ? ` (currently: ${JOB_LABELS[runningFortnoxJob.name] || runningFortnoxJob.name})` : ''}.
                  </p>
                </div>
                <JobTable
                  jobs={fortnoxJobs}
                  runningName={runningName}
                  onRun={handleRun}
                  blockedReason={(job) =>
                    runningFortnoxJob && runningFortnoxJob.name !== job.name
                      ? `Waiting for ${JOB_LABELS[runningFortnoxJob.name] || runningFortnoxJob.name} to finish`
                      : undefined
                  }
                />
              </div>
            )}

            {otherJobs.length > 0 && (
              <div className="space-y-2">
                <h2 className="text-sm font-semibold text-foreground">Other jobs</h2>
                <JobTable jobs={otherJobs} runningName={runningName} onRun={handleRun} />
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}
